import React, { useState, useRef, useEffect } from 'react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Check, X, Edit3, CalendarIcon, Loader2 } from "lucide-react";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { MultiSelect } from "@/components/ui/multi-select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";

interface EditableTextCellProps {
  row: any;
  field: string;
  placeholder?: string;
  isPending: (id: string, field?: string) => boolean;
  applyOptimisticUpdate: (id: string, field: string, value: any) => void;
  isOptimisticProcessing: boolean;
}

interface SearchableSelectCellProps {
  row: any;
  field: string;
  options: string[];
  badgeVariant?: string;
  isPending: (id: string, field?: string) => boolean;
  applyOptimisticUpdate: (id: string, field: string, value: any) => void;
  isOptimisticProcessing: boolean;
}

interface EditableDateCellProps {
  row: any;
  field: string;
  isPending: (id: string, field?: string) => boolean;
  applyOptimisticUpdate: (id: string, field: string, value: any) => void;
  isOptimisticProcessing: boolean;
}

interface MultiSelectCellProps {
  row: any;
  field: string;
  options: { value: string; label: string }[];
  onChange: (rowId: string, values: string[]) => void;
  placeholder?: string;
}

export const renderEditableTextCell = ({
  row,
  field,
  placeholder = "Enter value...",
  isPending,
  applyOptimisticUpdate,
  isOptimisticProcessing
}: EditableTextCellProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(row[field]?.toString() || '');
  const [hasChanged, setHasChanged] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { hasBuAccess } = useAuth();

  const originalValue = row[field]?.toString() || '';

  useEffect(() => {
    if (!isEditing) {
      setEditValue(originalValue);
      setHasChanged(false);
    }
  }, [originalValue, isEditing]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleStartEdit = () => {
    if (hasBuAccess) return; // Prevent editing for BU Access users
    setIsEditing(true);
    setEditValue(originalValue);
    setHasChanged(false);
  };

  const handleSave = () => {
    if (hasBuAccess) return; // Prevent saving for BU Access users
    if (hasChanged && editValue !== originalValue) {
      applyOptimisticUpdate(row.id, field, editValue);
    }
    setIsEditing(false);
    setHasChanged(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(originalValue);
    setHasChanged(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setEditValue(newValue);
    setHasChanged(newValue !== originalValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const isCurrentlyPending = isPending(row.id, field);
  const displayValue = originalValue || '';
  const isEmpty = !displayValue;

  // For BU Access users, show read-only content
  if (hasBuAccess) {
    return (
      <div className="px-2 py-1">
        <div className="text-sm text-gray-900">
          {isEmpty ? 'Not set' : displayValue}
        </div>
      </div>
    );
  }

  if (!isEditing) {
    return (
      <div className="relative px-2 py-1">
        {isCurrentlyPending && (
          <div className="absolute -top-1 -right-1 z-10">
            <Badge variant="secondary" className="h-4 px-1 text-xs bg-yellow-100 text-yellow-800">
              <Loader2 className="h-2 w-2 animate-spin mr-1" />
              Saving
            </Badge>
          </div>
        )}
        <div 
          className={cn(
            "group flex items-center gap-2 min-h-[32px] px-2 py-1 rounded cursor-pointer",
            "hover:bg-muted/50 transition-colors",
            isCurrentlyPending && "opacity-50"
          )}
          onClick={handleStartEdit}
        >
          <span className={cn(
            "flex-1 text-xs truncate",
            isEmpty && "text-muted-foreground italic"
          )}>
            {isEmpty ? placeholder : displayValue}
          </span>
          <Edit3 className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity flex-shrink-0" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-2 py-1">
      <div className={cn(
        "flex items-center gap-1 p-1 bg-background border rounded",
        "ring-2 ring-primary/20 border-primary/30"
      )}>
        <Input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="h-6 text-xs border-0 bg-transparent focus-visible:ring-0 px-1 flex-1"
        />
        <div className="flex gap-1 flex-shrink-0">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={handleSave}
            disabled={!hasChanged}
            className={cn(
              "h-5 w-5 p-0 hover:bg-green-100 hover:text-green-700",
              "dark:hover:bg-green-900 dark:hover:text-green-300",
              hasChanged && "text-green-600 dark:text-green-400"
            )}
          >
            <Check className="h-3 w-3" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={handleCancel}
            className="h-5 w-5 p-0 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-900 dark:hover:text-red-300"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export const renderSearchableSelectCell = ({
  row,
  field,
  options,
  badgeVariant = "default",
  isPending,
  applyOptimisticUpdate,
  isOptimisticProcessing
}: SearchableSelectCellProps) => {
  const currentValue = row[field] || '';
  const isCurrentlyPending = isPending(row.id, field);
  const { hasBuAccess } = useAuth();

  const handleChange = (value: string) => {
    if (hasBuAccess) return; // Prevent changes for BU Access users
    if (value !== currentValue) {
      applyOptimisticUpdate(row.id, field, value);
    }
  };

  // For BU Access users, show read-only badge
  if (hasBuAccess) {
    return (
      <div className="px-2 py-1">
        <div className="inline-flex items-center px-2 py-1 rounded-md bg-gray-50 text-gray-700 text-xs font-medium border border-gray-200">
          {currentValue || 'Not set'}
        </div>
      </div>
    );
  }

  return (
    <div className="relative px-2 py-1">
      {isCurrentlyPending && (
        <div className="absolute -top-1 -right-1 z-10">
          <Badge variant="secondary" className="h-4 px-1 text-xs bg-yellow-100 text-yellow-800">
            <Loader2 className="h-2 w-2 animate-spin mr-1" />
            Saving
          </Badge>
        </div>
      )}
      <SearchableSelect
        options={options}
        value={currentValue}
        onChange={handleChange}
        placeholder={`Select ${field}...`}
        badgeVariant={badgeVariant as any}
        className="w-full"
        disabled={isCurrentlyPending}
        clearable={true}
      />
    </div>
  );
};

export const renderEditableDateCell = ({
  row,
  field,
  isPending,
  applyOptimisticUpdate,
  isOptimisticProcessing
}: EditableDateCellProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const currentValue = row[field];
  const dateValue = currentValue instanceof Date ? currentValue : 
                   currentValue ? new Date(currentValue) : undefined;
  const isCurrentlyPending = isPending(row.id, field);
  const { hasBuAccess } = useAuth();

  const handleDateSelect = (date: Date | undefined) => {
    if (hasBuAccess) return; // Prevent changes for BU Access users
    if (date) {
      applyOptimisticUpdate(row.id, field, date);
    } else {
      applyOptimisticUpdate(row.id, field, null);
    }
    setIsOpen(false);
  };

  // For BU Access users, show read-only date
  if (hasBuAccess) {
    return (
      <div className="px-2 py-1">
        <div className="text-sm text-gray-900">
          {dateValue ? dateValue.toLocaleDateString() : 'Not set'}
        </div>
      </div>
    );
  }

  return (
    <div className="relative px-2 py-1">
      {isCurrentlyPending && (
        <div className="absolute -top-1 -right-1 z-10">
          <Badge variant="secondary" className="h-4 px-1 text-xs bg-yellow-100 text-yellow-800">
            <Loader2 className="h-2 w-2 animate-spin mr-1" />
            Saving
          </Badge>
        </div>
      )}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-full justify-start text-left font-normal h-8 text-xs",
              !dateValue && "text-muted-foreground",
              isCurrentlyPending && "opacity-50"
            )}
            disabled={isCurrentlyPending}
          >
            <CalendarIcon className="mr-2 h-3 w-3" />
            {dateValue ? format(dateValue, "PPP") : "Pick a date"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={dateValue}
            onSelect={handleDateSelect}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

export const renderMultiSelectCell = ({
  row,
  field,
  options,
  onChange,
  placeholder = "Select options..."
}: MultiSelectCellProps) => {
  const currentValues = Array.isArray(row[field]) ? row[field] : [];
  const { hasBuAccess } = useAuth();

  const handleChange = (selectedValues: string[]) => {
    if (hasBuAccess) return; // Prevent changes for BU Access users
    onChange(row.id, selectedValues);
  };

  // For BU Access users, show read-only badges
  if (hasBuAccess) {
    return (
      <div className="px-2 py-1">
        <div className="flex flex-wrap gap-1">
          {currentValues.length > 0 ? currentValues.map((value: string, index: number) => (
            <div key={index} className="inline-flex items-center px-2 py-1 rounded-md bg-gray-50 text-gray-700 text-xs font-medium border border-gray-200">
              {value}
            </div>
          )) : (
            <span className="text-xs text-gray-500">No options selected</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="px-2 py-1">
      <MultiSelect
        options={options}
        selected={currentValues}
        onChange={handleChange}
        placeholder={placeholder}
        maxDisplay={2}
        className="w-full"
      />
    </div>
  );
};
